import {
  Box,
  Button,
  Dialog,
  Grid,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import {
  Cancel,
  SearchOutlined,
  Share,
  InfoOutlined,
  ExpandMore,
  ExpandLess,
  DeleteOutline,
} from "@mui/icons-material";
import { Share2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import CustomStyledRadio from "../ivr/CustomStyledRadio";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import Table from "../../common/CustomTable";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  fetchShareInvoiceList,
  SearchShareInvoiceInfo,
  useInvoiceSlice,
  clearBulkPaymentInvoiceList,
  invoiceInfo,
} from "../../../redux/slice/invoice/InvoiceSlice";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import CustomTextField from "../../common/CustomTextField";
import CustomEmailChipField from "../../common/CustomEmailChipField";
import { shareInvoices, ShareInvoiceRequest } from "../../../service/InvoiceService";
import { useModalStyles } from "../../common/useModalStyles";
import CustomInvoiceAging from "../../common/CustomInvoiceAging";
import { priceFormatter, renderDynamicAgingColumn } from "../../../utils/Utility";
import CustomTooltip from "../../common/CustomTooltip";

// CS-1666 - Share Invoice Feature
interface ShareInvoicePopupProps {
  open: boolean;
  onClose: () => void;
  onResetSelection?: () => void;
  practiceOptions?: { label: string; value: any }[];
  manufacturerOptions?: { label: string; value: any }[];
}

interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  align?: "right" | "center" | "left";
}

interface InvoiceTableRow {
  uniqueId?: number;
  invoiceId: number;
  mfgInvoiceNumber: string;
  checkBox?: JSX.Element;
  emailInput?: JSX.Element;
  invoiceAmount: string;
  remainingAmount: string;
  invoiceLateDays?: JSX.Element | string;
  // CS-1666 - Practice name column for Share Invoice grid
  practiceName?: string;
  orderNumber?: string;
  invoiceStatus?: string;
  invoiceDueDate?: string;
  mfgInvoiceDate?: string;
  manufacturerName?: string | JSX.Element;
}

interface SavedSelection {
  invoiceId: number;
  mfgInvoiceNumber: string;
  emails: string[];
}

const normalizeEmailList = (emails?: string[]) =>
  Array.from(new Set((Array.isArray(emails) ? emails : []).filter(Boolean)));

// Memoized email input component to prevent entire table re-render on keystroke
const InvoiceEmailCell = React.memo(function InvoiceEmailCell({
  control,
  controllerName,
  isChecked,
  trigger,
}: {
  control: any;
  controllerName: string;
  isChecked: boolean;
  trigger: (name?: string | string[]) => Promise<boolean>;
}) {
  return (
    <Box sx={{ minWidth: 220 }}>
      <CustomEmailChipField
        control={control}
        returnAsArray={true}
        controllerName={controllerName}
        isDisabled={!isChecked}
        rules={{
          validate: (value: string[]) =>
            !isChecked || (Array.isArray(value) && value.length > 0) || "At least one valid email is required",
        }}
        onBlur={() => {
          if (isChecked) {
            void trigger(controllerName);
          }
        }}
        placeholder={isChecked ? "Enter email" : "Select invoice to add recipients"}
      />
    </Box>
  );
});

const ShareInvoicePopup: React.FC<ShareInvoicePopupProps> = ({
  open,
  onClose,
  onResetSelection,
  practiceOptions = [],
  manufacturerOptions = [],
}) => {
  const INITIAL_VISIBLE_INVOICES = 50;
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    resetField,
    clearErrors,
    setError,
    trigger,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapWithLabelSx,
    inputFieldWithLabelSx,
    primaryBtnSx,
    cancelBtnSx,
    backBtnSx,
  } = useModalStyles();
  const dispatch = useAppDispatch();
  const { bulkPaymentInvoiceList } = useInvoiceSlice();

  const [sharingMode, setSharingMode] = useState<"GROUP" | "INDIVIDUAL">("GROUP");
  const [emailChips, setEmailChips] = useState<Record<number, string[]>>({});
  const [groupEmails, setGroupEmails] = useState<string[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [savedSelections, setSavedSelections] = useState<SavedSelection[]>([]);
  const [showSavedSelections, setShowSavedSelections] = useState(false);
  const [visibleInvoiceCount, setVisibleInvoiceCount] = useState(INITIAL_VISIBLE_INVOICES);

  // Reset dynamic invoice form controls including dirty/touched/error state.
  const resetInvoiceDynamicState = useCallback(() => {
    setValue("invoiceCheckbox", {}, { shouldValidate: false, shouldDirty: false });
    resetField("invoiceEmails", { defaultValue: {} });
    clearErrors("invoiceEmails");
  }, [setValue, resetField, clearErrors]);

  // CS-1666 Sharing mode options
  const sharingModeOptions = useMemo(
    () => [
      { label: "Send to the Same Recipient(s)", value: "GROUP", subLabel: "Use one recipient list for all selected invoices (same practice only)." },
      { label: "Send to Different Recipient(s)", value: "INDIVIDUAL", subLabel: "Assign recipient(s) individually for each invoice." },
    ],
    []
  );

  // Initialize sharingMode form value
  useEffect(() => {
    setValue("sharingMode", "GROUP");
  }, [setValue]);

  // Auto-select manufacturer and practice when only one option is available
  useEffect(() => {
    if (manufacturerOptions?.length === 1 && !watch("mfgId")) {
      setValue("mfgId", manufacturerOptions[0].value);
    }
    if (practiceOptions?.length === 1 && !watch("practiceId")) {
      setValue("practiceId", practiceOptions[0].value);
    }
  }, [manufacturerOptions, practiceOptions, setValue, watch]);

  // Clear validation errors when switching modes
  useEffect(() => {
    if (sharingMode === "INDIVIDUAL") {
      // Clear GROUP mode validation errors
      setValue("mfgId", watch("mfgId"), { shouldValidate: false });
      setValue("practiceId", watch("practiceId"), { shouldValidate: false });
    }
  }, [sharingMode, setValue, watch]);

  const invoiceCheckboxes = useWatch({
    control,
    name: "invoiceCheckbox",
  });
  const invoiceEmails = useWatch({
    control,
    name: "invoiceEmails",
  }) as Record<string, string[]> | undefined;
  const groupEmailsField = useWatch({
    control,
    name: "groupEmailsField",
  }) as string[] | undefined;

  const savedSelectionsById = useMemo(
    () =>
      savedSelections.reduce<Record<number, SavedSelection>>((acc, selection) => {
        acc[selection.invoiceId] = selection;
        return acc;
      }, {}),
    [savedSelections]
  );

  const getInvoiceEmails = useCallback(
    (invoiceId: number) =>
      normalizeEmailList(
        invoiceEmails?.[invoiceId] ??
          emailChips[invoiceId] ??
          savedSelectionsById[invoiceId]?.emails ??
          []
      ),
    [emailChips, invoiceEmails, savedSelectionsById]
  );

  // Keep legacy email state in sync with shared email chip field values.
  // This avoids rewriting downstream save/merge logic.
  useEffect(() => {
    if (sharingMode !== "INDIVIDUAL") return;
    const normalized: Record<number, string[]> = {};
    Object.entries(invoiceEmails || {}).forEach(([invoiceId, emails]) => {
      normalized[Number(invoiceId)] = Array.isArray(emails) ? emails : [];
    });
    setEmailChips(normalized);
  }, [invoiceEmails, sharingMode]);

  // Keep GROUP mode recipients in sync with the shared chip component field value.
  useEffect(() => {
    if (sharingMode !== "GROUP") return;
    setGroupEmails(Array.isArray(groupEmailsField) ? groupEmailsField : []);
  }, [groupEmailsField, sharingMode]);

  // CS-1666 - Save current selections only for INDIVIDUAL mode.
  // GROUP mode should behave as a fresh same-practice batch on every search.
  const saveCurrentSelections = useCallback(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return;

    if (sharingMode === "INDIVIDUAL") {
      const selectedInvoices = bulkPaymentInvoiceList.filter(
        (invoice: invoiceInfo) => invoiceCheckboxes?.[invoice.invoiceId] === true
      );

      if (selectedInvoices.length === 0) return;

      setSavedSelections((prev) => {
        const nextSelectionsById = prev.reduce<Record<number, SavedSelection>>((acc, selection) => {
          acc[selection.invoiceId] = selection;
          return acc;
        }, {});

        selectedInvoices.forEach((invoice: invoiceInfo) => {
          const existingSelection = nextSelectionsById[invoice.invoiceId];
          const nextEmails = getInvoiceEmails(invoice.invoiceId);

          nextSelectionsById[invoice.invoiceId] = {
            invoiceId: invoice.invoiceId,
            mfgInvoiceNumber: invoice.mfgInvoiceNumber || existingSelection?.mfgInvoiceNumber || `INV-${invoice.invoiceId}`,
            emails: nextEmails,
          };
        });

        return Object.values(nextSelectionsById);
      });
      setShowSavedSelections(true);
    } else {
      // CS-1666 - Do not persist selections for GROUP mode across searches.
      // A new manufacturer/practice search defines a new group context.
      return;
    }
  }, [bulkPaymentInvoiceList, getInvoiceEmails, invoiceCheckboxes, sharingMode]);

  // Handle search - call invoice search API
  const handleSearch = async () => {
    const practiceId = watch("practiceId");
    const mfgId = watch("mfgId");
    const mfgInvoiceNumber = watch("mfgInvoiceNumber");
    // CS-1666 - Capture invoice age range filter values for Share Invoice search
    const invoiceAgeMin = watch("invoiceAgeMin");
    const invoiceAgeMax = watch("invoiceAgeMax");

    // Validation for GROUP mode - required only for Search action (not final Share submit)
    if (sharingMode === "GROUP") {
      let hasGroupSearchError = false;
      if (!mfgId) {
        setError("mfgId", { type: "required", message: "Manufacturer is required" });
        hasGroupSearchError = true;
      } else {
        clearErrors("mfgId");
      }
      if (!practiceId) {
        setError("practiceId", { type: "required", message: "Practice is required" });
        hasGroupSearchError = true;
      } else {
        clearErrors("practiceId");
      }
      if (hasGroupSearchError) return;
    }

    // CS-1666 - Preserve selections only for INDIVIDUAL mode.
    // GROUP mode should reset selection and recipients when a new search runs.
    if (sharingMode === "INDIVIDUAL") {
      saveCurrentSelections();
    } else {
      setSavedSelections([]);
      setShowSavedSelections(false);
    }

    // Clear Redux state first
    dispatch(clearBulkPaymentInvoiceList());

    // Clear invoice selection/email state including dirty/touched/errors
    resetInvoiceDynamicState();

    // Clear email states for new search
    setEmailChips({});
    setGroupEmails([]);
    setValue("groupEmailsField", [], { shouldValidate: false, shouldDirty: false });
    setHasSearched(true);
    setVisibleInvoiceCount(INITIAL_VISIBLE_INVOICES);

    const searchParams: SearchShareInvoiceInfo = {
      mfgInvoiceNumber: mfgInvoiceNumber || null,
      mfgIds: mfgId ? [mfgId] : [],
      practiceIds: practiceId ? [practiceId] : [],
      // CS-1666 - Pass invoice age range to share invoice list API
      invoiceAgeMin: invoiceAgeMin || "",
      invoiceAgeMax: invoiceAgeMax || "",
    };

    try {
      await dispatch(fetchShareInvoiceList(searchParams));
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  // Calculate active invoices (checked)
  const activeInvoices = useMemo(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return [];

    const active = bulkPaymentInvoiceList.filter((invoice: invoiceInfo) => {
      const isChecked = invoiceCheckboxes?.[invoice.invoiceId] === true;
      return isChecked;
    });
    return active;
  }, [bulkPaymentInvoiceList, invoiceCheckboxes]);

  // Only map the currently visible invoice slice.
  // This avoids building heavy row/email controls for the full dataset on every render.
  const visibleInvoices = useMemo(
    () => (bulkPaymentInvoiceList || []).slice(0, visibleInvoiceCount),
    [bulkPaymentInvoiceList, visibleInvoiceCount]
  );

  // Format rows for CustomTable (visible slice only)
  const displayedInvoiceRows: InvoiceTableRow[] = useMemo(() => {
    if (!visibleInvoices || visibleInvoices.length === 0) return [];

    return visibleInvoices.map((invoice: invoiceInfo) => {
      const isChecked = invoiceCheckboxes?.[invoice.invoiceId] === true;
      const invoiceAmount = invoice.invoiceAmount || 0;
      const remainingAmount = invoice.remainingAmount || 0;

      const row: InvoiceTableRow = {
        uniqueId: invoice.invoiceId,
        invoiceId: invoice.invoiceId,
        mfgInvoiceNumber: invoice.mfgInvoiceNumber || `INV-${invoice.invoiceId}`,
        invoiceAmount: priceFormatter(Number(invoiceAmount)),
        remainingAmount: priceFormatter(Number(remainingAmount)),
        // CS-1666 - Show INV Aging in Share Invoice grid, same as Invoice List
        invoiceLateDays: renderDynamicAgingColumn(invoice),
        // CS-1666 - Practice name column in Share Invoice grid (truncated with tooltip)
        practiceName: invoice.practiceName ? (
          <CustomTooltip title={invoice.practiceName} arrow placement="top">
            <Typography
              variant="body2"
              noWrap
              sx={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {invoice.practiceName}
            </Typography>
          </CustomTooltip>
        ) : '' as any,
        orderNumber: invoice.orderNumber || "-",
        invoiceStatus: invoice.invoiceStatus || "-",
        invoiceDueDate: invoice.invoiceDueDate || "-",
        mfgInvoiceDate: invoice.mfgInvoiceDate || "-",
        manufacturerName: invoice.manufacturerName ? (
          <CustomTooltip title={invoice.manufacturerName} arrow placement="top">
            <Typography
              variant="body2"
              noWrap
              sx={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {invoice.manufacturerName}
            </Typography>
          </CustomTooltip>
        ) : '-' as any,
      };

      // Add checkbox for both modes
      row.checkBox = (
        <CustomCheckbox
          control={control}
          controllerName={`invoiceCheckbox.${invoice.invoiceId}`}
          onChange={(e) => {
            if (e.target.checked) {
              const existingEmails = getInvoiceEmails(invoice.invoiceId);
              if (existingEmails.length > 0) {
                setValue(`invoiceEmails.${invoice.invoiceId}`, existingEmails, {
                  shouldValidate: false,
                  shouldDirty: false,
                });
                setEmailChips((prev) => ({
                  ...prev,
                  [invoice.invoiceId]: existingEmails,
                }));
              }
            }
          }}
        />
      );

      // In INDIVIDUAL mode, always show recipient field.
      if (sharingMode === "INDIVIDUAL") {
        // Performance: render the heavy email chip input only for checked rows.
        // Unchecked rows keep a lightweight read-only placeholder.
        row.emailInput = isChecked ? (
          <InvoiceEmailCell
            control={control}
            controllerName={`invoiceEmails.${invoice.invoiceId}`}
            isChecked={isChecked}
            trigger={trigger}
          />
        ) : (
          <Box
            sx={{
              minWidth: 220,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              border: `1px solid ${tokens.inputBorder}`,
              color: tokens.textSecondary,
              fontSize: "0.875rem",
              backgroundColor: tokens.inputBg,
            }}
          >
            Select invoice to add recipients
          </Box>
        );
      }

      return row;
    });
  }, [visibleInvoices, invoiceCheckboxes, control, sharingMode, tokens, trigger, getInvoiceEmails, setValue]);

  // Handle select all checkbox
  const handleSelectAllCheckBox = (checked: boolean) => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return;

    const nextCheckboxState = bulkPaymentInvoiceList.reduce<Record<number, boolean>>((acc, invoice: invoiceInfo) => {
      acc[invoice.invoiceId] = checked;
      return acc;
    }, {});
    setValue("invoiceCheckbox", nextCheckboxState, { shouldValidate: false, shouldDirty: true });

    if (sharingMode === "INDIVIDUAL" && checked) {
      bulkPaymentInvoiceList.forEach((invoice: invoiceInfo) => {
        const existingEmails = getInvoiceEmails(invoice.invoiceId);
        if (existingEmails.length > 0) {
          setValue(`invoiceEmails.${invoice.invoiceId}`, existingEmails, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      });
    }
  };

  // Calculate if all invoices are selected
  const allInvoicesSelected = useMemo(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return false;

    return bulkPaymentInvoiceList.every(
      (invoice: invoiceInfo) => invoiceCheckboxes?.[invoice.invoiceId] === true
    );
  }, [bulkPaymentInvoiceList, invoiceCheckboxes]);

  // Remove a saved selection
  const handleRemoveSavedSelection = useCallback((invoiceId: number) => {
    setSavedSelections((prev) => prev.filter((s) => s.invoiceId !== invoiceId));
  }, []);

  // Define columns for CustomTable - with minWidth to prevent header chopping
  const invoiceColumns: Column[] = useMemo(() => {
    const isIndividual = sharingMode === "INDIVIDUAL";
    const baseColumns: Column[] = [
      {
        id: "checkBox",
        label: (
          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox
              control={control}
              controllerName="selectAllCheckBox"
              checked={allInvoicesSelected}
              isDisabled={!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0}
              onChange={(e) => handleSelectAllCheckBox(e.target.checked)}
              sx={{ align: "center" }}
            />
          </Box>
        ),
        minWidth: 32,
      },
      {
        id: "mfgInvoiceNumber",
        label: "INV ID",
        minWidth: isIndividual ? 95 : 150,
      },
      {
        id: "mfgInvoiceDate",
        label: "INV Date",
        minWidth: isIndividual ? 80 : 120,
      },
      {
        id: "invoiceDueDate",
        label: "INV Due Date",
        minWidth: isIndividual ? 80 : 120,
      },
      {
        id: "invoiceLateDays",
        label: "INV Aging",
        minWidth: isIndividual ? 70 : 120,
      },
      ...(isIndividual
        ? [
            {
              id: "manufacturerName",
              label: "MFR",
              minWidth: 80,
            },
            {
              id: "practiceName",
              label: "Practice",
              minWidth: 80,
            },
          ]
        : []),
      {
        id: "invoiceAmount",
        label: "Inv Total",
        minWidth: isIndividual ? 80 : 130,
      },
      {
        id: "remainingAmount",
        label: "Outstd. Amt",
        minWidth: isIndividual ? 80 : 150,
      },
      {
        id: "invoiceStatus",
        label: "Status",
        minWidth: isIndividual ? 65 : 100,
      },
    ];

    // Add email column only for INDIVIDUAL mode
    if (isIndividual) {
      baseColumns.push({
        id: "emailInput",
        label: "Email Recipients",
        minWidth: 220,
      });
    }

    return baseColumns;
  }, [sharingMode, control, bulkPaymentInvoiceList]);

  const currentInvoiceShareList = useMemo(
    () =>
      sharingMode === "GROUP"
        ? activeInvoices.map((invoice: invoiceInfo) => ({
          invoiceId: invoice.invoiceId,
          emails: groupEmails,
        }))
        : activeInvoices.map((invoice: invoiceInfo) => ({
          invoiceId: invoice.invoiceId,
          emails: getInvoiceEmails(invoice.invoiceId),
        })),
    [sharingMode, activeInvoices, groupEmails, getInvoiceEmails]
  );

  const mergedInvoiceShareList = useMemo(() => {
    // CS-1666 - GROUP mode should only submit invoices from the current search context.
    if (sharingMode === "GROUP") {
      return currentInvoiceShareList.filter((i) => i.emails.length > 0);
    }

    const shareDetailsByInvoiceId = new Map<number, { invoiceId: number; emails: string[] }>();

    savedSelections.forEach((selection) => {
      shareDetailsByInvoiceId.set(selection.invoiceId, {
        invoiceId: selection.invoiceId,
        emails: normalizeEmailList(selection.emails),
      });
    });

    currentInvoiceShareList.forEach((currentSelection) => {
      shareDetailsByInvoiceId.set(currentSelection.invoiceId, {
        invoiceId: currentSelection.invoiceId,
        emails: normalizeEmailList(currentSelection.emails),
      });
    });

    return Array.from(shareDetailsByInvoiceId.values()).filter((i) => i.emails.length > 0);
  }, [currentInvoiceShareList, savedSelections, sharingMode]);

  const totalSelectedInvoiceCount = useMemo(() => {
    if (sharingMode === "GROUP") {
      return activeInvoices.length;
    }

    const selectedInvoiceIds = new Set<number>(savedSelections.map((selection) => selection.invoiceId));
    activeInvoices.forEach((invoice: invoiceInfo) => {
      selectedInvoiceIds.add(invoice.invoiceId);
    });

    return selectedInvoiceIds.size;
  }, [activeInvoices, savedSelections, sharingMode]);
  const hasNoSelectedInvoices = totalSelectedInvoiceCount === 0;

  const hasNoEmails = useMemo(() => {
    if (sharingMode === "GROUP") {
      return activeInvoices.length > 0 && groupEmails.length === 0;
    }

    const currentHasNoEmail = activeInvoices.some(
      (invoice: invoiceInfo) => getInvoiceEmails(invoice.invoiceId).length === 0
    );
    const savedHasNoEmail = savedSelections.some((s) => !s.emails || s.emails.length === 0);
    return currentHasNoEmail || savedHasNoEmail;
  }, [sharingMode, activeInvoices, getInvoiceEmails, groupEmails, savedSelections]);

  const isSubmitDisabled =
    hasNoSelectedInvoices ||
    hasNoEmails ||
    !watch("subject") ||
    !watch("customMessage");

  const onSubmit = async (data: any) => {
    if (sharingMode === "INDIVIDUAL") {
      clearErrors(["mfgId", "practiceId"]);
    }

    // Prepare request payload
    const requestPayload: ShareInvoiceRequest = {
      subject: data.subject,
      customMessage: data.customMessage,
      invoiceShareDetailList: mergedInvoiceShareList,
    };


    try {
      const response = await shareInvoices(requestPayload);

      if (response && response.data) {
        const alertObj: SingleAlertInfo = {
          message: "Invoices shared successfully!",
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
        handleClose();
      } else if (response && response.errors && response.errors.length > 0) {
        const alertObj: SingleAlertInfo = {
          message: response.errors[0].errorMessage || "Failed to share invoices",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      }
    } catch (error) {
      const alertObj: SingleAlertInfo = {
        message: "An error occurred while sharing invoices. Please try again.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
  };

  // Handle dialog close with cleanup
  const handleClose = () => {
    reset();
    dispatch(clearBulkPaymentInvoiceList());
    resetInvoiceDynamicState();
    setEmailChips({});
    setGroupEmails([]);
    setValue("groupEmailsField", [], { shouldValidate: false, shouldDirty: false });
    setSavedSelections([]);
    setShowSavedSelections(false);
    setVisibleInvoiceCount(INITIAL_VISIBLE_INVOICES);
    setSharingMode("GROUP");
    setHasSearched(false);

    if (onResetSelection) {
      onResetSelection();
    }

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="share-invoice-dialog-title"
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
            e.preventDefault();
          }
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
          maxHeight: isMobile ? "100dvh" : "92vh",
        }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <Share2 size={22} color={tokens.accent} strokeWidth={1.8} />
          </Box>
          <Box sx={{ minWidth: 0, pr: 4 }}>
            <Typography
              id="share-invoice-dialog-title"
              fontWeight={700}
              sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
            >
              Share Invoice
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              Share invoices with recipients via email
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            flex: 1,
            minHeight: 0,
            maxHeight: { xs: "calc(100dvh - 200px)", sm: "min(80vh, calc(92vh - 220px))" },
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
            borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                  Choose How to Send Invoices <Box component="span" sx={{ color: tokens.accent }}>*</Box>
                </Typography>
                <CustomStyledRadio
                  control={control}
                  controllerName="sharingMode"
                  defaultValue="GROUP"
                  options={sharingModeOptions}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    // Save current selections before switching modes
                    saveCurrentSelections();
                    setSharingMode(value);
                    setValue("sharingMode", value);
                    // Clear email states when switching modes
                    setEmailChips({});
                    setGroupEmails([]);
                    setValue("groupEmailsField", [], { shouldValidate: false, shouldDirty: false });
                    // Clear saved selections on mode change
                    setSavedSelections([]);
                    setShowSavedSelections(false);
                    setVisibleInvoiceCount(INITIAL_VISIBLE_INVOICES);
                    // Clear search results and reset search state
                    dispatch(clearBulkPaymentInvoiceList());
                    setHasSearched(false);
                    // Clear form values and validation errors
                    clearErrors();
                    setValue("mfgId", undefined);
                    setValue("practiceId", undefined);
                    setValue("mfgInvoiceNumber", "");
                    setValue("invoiceAgeMin", "");
                    setValue("invoiceAgeMax", "");
                    setValue("selectAllCheckBox", false);
                    resetInvoiceDynamicState();
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ borderColor: tokens.border }} />
              </Grid>

              {/* Search filters: same modal field styling (dropdown / text / age); Search + actions right-aligned */}
              <Grid item xs={12}>
                <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                  Search Invoices
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "flex-end",
                  }}
                >
                  <Box sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 200 } }}>
                    <Box sx={dropdownWrapWithLabelSx()}>
                      <CustomDropdownField
                        control={control}
                        name="mfgId"
                        label="Manufacturer"
                        options={manufacturerOptions}
                        disabled={manufacturerOptions?.length === 1}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 200 } }}>
                    <Box sx={dropdownWrapWithLabelSx()}>
                      <CustomDropdownField
                        control={control}
                        name="practiceId"
                        label="Practice"
                        options={practiceOptions}
                        disabled={practiceOptions?.length === 1}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 200 } }}>
                    <Box sx={inputFieldWithLabelSx()}>
                      <CustomTextField
                        control={control}
                        controllerName="mfgInvoiceNumber"
                        label="Invoice Number"
                        placeholder="Enter invoice number"
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 200 } }}>
                    <Box sx={inputFieldWithLabelSx()}>
                      <CustomInvoiceAging
                        outlinedModal
                        label="Invoice Age Range"
                        control={control}
                        minControllerName="invoiceAgeMin"
                        maxControllerName="invoiceAgeMax"
                        minLabel="Min Age"
                        maxLabel="Max Age"
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flex: "0 0 auto",
                      width: { xs: "100%", sm: "auto" },
                      ml: { xs: 0, sm: "auto" },
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SearchOutlined sx={{ fontSize: "1rem !important" }} />}
                      onClick={handleSearch}
                      sx={{
                        ...primaryBtnSx,
                        minWidth: 110,
                        minHeight: 35,
                        height: 35,
                        py: 0,
                        px: 1.25,
                        fontSize: "0.8125rem",
                        lineHeight: 1,
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ borderColor: tokens.border }} />
              </Grid>

              {/* Group Email Input (only for GROUP mode) */}
              {sharingMode === "GROUP" && bulkPaymentInvoiceList && bulkPaymentInvoiceList.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                      Email Recipients <Box component="span" sx={{ color: tokens.accent }}>*</Box>
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <CustomEmailChipField
                          control={control}
                          controllerName="groupEmailsField"
                          returnAsArray={true}
                          placeholder="Enter email address"
                          rules={{
                            validate: (value: string[]) =>
                              (Array.isArray(value) && value.length > 0) || "Add at least one email address to share invoices",
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      Add at least one email address to share invoices
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ borderColor: tokens.border }} />
                  </Grid>
                </>
              )}

              {/* Invoice Details Table */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                    Invoice Details
                  </Typography>
                  {bulkPaymentInvoiceList && bulkPaymentInvoiceList.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<Cancel sx={{ fontSize: "1rem !important" }} />}
                      onClick={() => {
                        // CS-1666 - Preserve selections only for INDIVIDUAL mode.
                        if (sharingMode === "INDIVIDUAL") {
                          saveCurrentSelections();
                        } else {
                          setSavedSelections([]);
                          setShowSavedSelections(false);
                        }
                        // Clear search results and reset state
                        dispatch(clearBulkPaymentInvoiceList());
                        setEmailChips({});
                        setGroupEmails([]);
                        setValue("groupEmailsField", [], { shouldValidate: false, shouldDirty: false });
                        setHasSearched(false);
                        setVisibleInvoiceCount(INITIAL_VISIBLE_INVOICES);
                        // Clear invoice selection/email state including dirty/touched/errors
                        resetInvoiceDynamicState();
                      }}
                      sx={cancelBtnSx}
                    >
                      Close Results
                    </Button>
                  )}
                </Box>
                {/* CS-1666 - Removed inner maxHeight/overflow to eliminate double scrollbar;
                    dialog scroll handles vertical overflow, table wrapper only handles horizontal overflow */}
                <Box
                  sx={{
                    overflowX: "auto",
                    border: `1px solid ${tokens.border}`,
                    borderRadius: "12px",
                    bgcolor: tokens.cardBg,
                    "& table": {
                      backgroundColor: tokens.cardBg,
                    },
                    "& th, & td": {
                      borderColor: tokens.border,
                    },
                    "& th": {
                      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : tokens.inputBg,
                    },
                  }}
                >
                  <Table
                    columns={invoiceColumns}
                    rows={displayedInvoiceRows}
                    disablePagination={true}
                    emptyText="No invoices found. Please search for invoices."
                    uniqueValue="ShareInvoice"
                    borderBox={false}
                    visibleHeader={true}
                    sx={{
                      "& .MuiTable-root": {
                        backgroundColor: `${tokens.cardBg} !important`,
                      },
                      "& .MuiTableCell-root": {
                        color: `${tokens.textPrimary} !important`,
                        borderBottom: `1px solid ${tokens.border}`,
                        padding: "6px 8px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "& .MuiTableCell-root:first-of-type": {
                        paddingLeft: "4px",
                        paddingRight: "0px",
                        width: "32px",
                        maxWidth: "32px",
                      },
                      "& .MuiTableHead-root .MuiTableCell-root": {
                        backgroundColor: `${isDark ? "rgba(32,38,54,0.95)" : tokens.inputBg} !important`,
                        color: `${tokens.textPrimary} !important`,
                      },
                      "& .MuiTableBody-root .MuiTableRow-root": {
                        backgroundColor: tokens.bg,
                      },
                      "& .MuiTableBody-root .MuiTableRow-root:hover": {
                        backgroundColor: isDark ? "rgba(255,255,255,0.05)" : tokens.cardBg,
                      },
                    }}
                  />
                </Box>
                {bulkPaymentInvoiceList && bulkPaymentInvoiceList.length > visibleInvoiceCount && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setVisibleInvoiceCount((prev) =>
                          Math.min(prev + INITIAL_VISIBLE_INVOICES, bulkPaymentInvoiceList.length)
                        )
                      }
                      sx={backBtnSx}
                    >
                      Load More ({bulkPaymentInvoiceList.length - visibleInvoiceCount} remaining)
                    </Button>
                  </Box>
                )}
              </Grid>

              {/* Saved Selections Section - shown after Invoice Details */}
              {sharingMode === "INDIVIDUAL" && savedSelections.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        mb: 1,
                      }}
                      onClick={() => setShowSavedSelections(!showSavedSelections)}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary, mr: 1 }}>
                        Previously Selected Invoices ({savedSelections.length})
                      </Typography>
                      {showSavedSelections ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                    <Collapse in={showSavedSelections}>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflow: "auto",
                          border: `1px solid ${tokens.border}`,
                          borderRadius: 1,
                          p: 1.5,
                          bgcolor: tokens.cardBg,
                        }}
                      >
                        {savedSelections.map((sel) => (
                          <Box
                            key={sel.invoiceId}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              py: 0.75,
                              px: 1.5,
                              mb: 0.5,
                              borderRadius: 1,
                              bgcolor: isDark ? "rgba(255,255,255,0.06)" : tokens.bg,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  minWidth: 120,
                                  color: tokens.textPrimary,
                                }}
                              >
                                {sel.mfgInvoiceNumber}
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {sel.emails.map((email) => (
                                  <Chip
                                    key={email}
                                    label={email}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: tokens.accent,
                                      color: tokens.accent,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                            <CustomTooltip title="Remove" placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveSavedSelection(sel.invoiceId)}
                                sx={{
                                  ml: 1,
                                  color: tokens.textSecondary,
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </CustomTooltip>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ borderColor: tokens.border }} />
                  </Grid>
                </>
              )}

              {/* Validation Messages */}
              {hasNoSelectedInvoices && (hasSearched || savedSelections.length > 0) && (
                <Grid item xs={12}>
                  <Typography variant="h5" color="warning.main" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <InfoOutlined fontSize="small" />
                    Please select at least one invoice to share.
                  </Typography>
                </Grid>
              )}

              {/* Validation for INDIVIDUAL mode - missing emails */}
              {sharingMode === "INDIVIDUAL" && !hasNoSelectedInvoices && hasNoEmails && (
                <Grid item xs={12}>
                  <Typography variant="h5" color="error" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <InfoOutlined fontSize="small" />
                    Please add at least one email address for each selected invoice.
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ borderColor: tokens.border }} />
              </Grid>

              {/* Email Message Section */}
              <Grid item xs={12}>
                <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                  Email Message
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={inputFieldWithLabelSx()}>
                      <CustomTextField
                        control={control}
                        controllerName="subject"
                        label="Subject"
                        placeholder="Enter email subject"
                        rules={{ required: "Subject is required" }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={inputFieldWithLabelSx()}>
                      <CustomTextField
                        control={control}
                        controllerName="customMessage"
                        label="Custom Message"
                        placeholder="Enter your message"
                        multiline
                        rows={4}
                        rules={{ required: "Custom message is required" }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Summary */}
              {totalSelectedInvoiceCount > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: tokens.cardBg,
                      borderRadius: 1,
                      border: `1px solid ${tokens.border}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.textPrimary }}>
                      Summary: {totalSelectedInvoiceCount} invoice{totalSelectedInvoiceCount !== 1 ? "s" : ""} selected
                      {sharingMode === "GROUP" && ` • ${groupEmails.length} recipient${groupEmails.length !== 1 ? "s" : ""}`}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: "flex-end",
              alignItems: { xs: "flex-end", sm: "center" },
            }}
          >
            <Button
              variant="contained"
              type="submit"
              startIcon={<Share sx={{ fontSize: "1rem !important" }} />}
              disabled={isSubmitDisabled}
              sx={{
                ...primaryBtnSx,
                minWidth: 130,
                ...(isSubmitDisabled && { opacity: 0.5, boxShadow: "none" }),
              }}
            >
              Share ({totalSelectedInvoiceCount} Invoice{totalSelectedInvoiceCount !== 1 ? "s" : ""})
            </Button>
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              sx={{ ...cancelBtnSx, minWidth: 110 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default ShareInvoicePopup;
